import React, { Fragment, useState } from "react";
import { Link, useParams } from "react-router-dom";
import moment from "moment";

import { useGetAssessmentDetails } from "../../../hooks/assessments";
import { useGetAssessmentPrograms } from "../../../hooks/assessment_programs";

import AssessmentDetailsLayout from "../../layouts/details/Assessment";
import { BoxNote } from "../../reusables/Cards";
import { SVGIcon } from "../../wrappers/SVGIcon";

import { getElipsisString } from "../../../helpers/utility";

import { IAssessmentProgramWithProgram } from "../../../interfaces/services";

import { Icons } from "../../../data/Assets";
import { AssessmentInviteWorker } from "../../sub_components/assessment/InviteWorker";

interface ICardInfoResponse {
    programDifficultyText: string;
    costDifficulty: string;
    costDifficultyText: string;
    timeframeDifficulty: string;
    timeframe: string;
};
const ImmigrationRecommendationsPage = () => {
    const { assessment_id } = useParams();

    const { data: assessment } = useGetAssessmentDetails(assessment_id ? Number(assessment_id) : null);
    const { data: assessmentPrograms, isLoading, error } = useGetAssessmentPrograms(assessment_id);

    const [mainView, setMainView] = useState<'recommendations' | 'invite'>('recommendations');

    const getCardInfo = (ap: IAssessmentProgramWithProgram): ICardInfoResponse => {
        let programDifficultyText = 'Easier';
        let costDifficulty = 'easy';
        let costDifficultyText = '$';
        let timeframeDifficulty = 'easy';
        let timeframe = '< 1 month';

        if (ap.program.difficulty === 'moderate') {
            programDifficultyText = 'Moderate';
        } else if (ap.program.difficulty === 'hard') {
            programDifficultyText = 'Hard';
        }

        if (ap.program.cost === 'two-star') {
            costDifficulty = 'moderate';
            costDifficultyText = '$$';
        } else if (ap.program.cost === 'three-star') {
            costDifficulty = 'hard';
            costDifficultyText = '$$$';
        }

        if (ap.program.timeframe === '1-3-month') {
            timeframeDifficulty = 'moderate';
            timeframe = '1-3 months';
        } else if (ap.program.timeframe === '3-6-month') {
            timeframeDifficulty = 'hard';
            timeframe = '3-6 months';
        } else if (ap.program.timeframe === '6month+') {
            timeframeDifficulty = 'harder';
            timeframe = '6 months+';
        }

        return {
            programDifficultyText,
            costDifficulty,
            costDifficultyText,
            timeframeDifficulty,
            timeframe,
        };
    };

    const getForeignWorkerName = () => {
        if (assessment?.foreign_worker) {
            return `${assessment?.foreign_worker?.worker_details[0]?.first_name} ${assessment?.foreign_worker?.worker_details[0]?.last_name ?? ''}`;
        } else if (assessment?.worker_name) {
            return assessment?.worker_name;
        }

        return "";
    };

    return (
        <AssessmentDetailsLayout
            operation='update'
            currentTab="Immigration Recommendations"
            title={assessment?.title ?? 'Assessment Information'}
            error={!!error}
        >
            {mainView === 'recommendations' &&
                <div className="immployer__assessment_recommendations__page_view">
                    <div className="immployer__recommendations__immigration_options__container">
                        <div className="immployer__recommendations__immigration_options__header">
                            <h4 className="pb_10 wt_500">Immigration Options</h4>
                            <div className={`immployer__recommendations__immigration_options__header__status ${!assessment?.report_url ? 'immployer__recommendations__immigration_options__header__status--in_progress' : 'immployer__recommendations__immigration_options__header__status--completed'}`}>
                                {!assessment?.report_url ? 'In Progress' : 'Completed'}
                            </div>
                            {!!assessment?.first_invitation_date
                                ? <div className="immployer__recommendations__immigration_options__header__invitation_status">
                                    <p>Invited: {moment(assessment.first_invitation_date).format('DD MMM YYYY')}</p>
                                    {!!assessment?.total_reminders && <p>Last reminder: {assessment?.latest_invitation_date ? moment(assessment?.latest_invitation_date).format('DD MMM YYYY') : 'N/A'}</p>}
                                    {!!assessment?.total_reminders && <p>{assessment?.total_reminders} reminders</p>}
                                </div>
                                : <button className="immployer__recommendations__immigration_options__header__invite_btn" onClick={() => setMainView('invite')}>Invite</button>
                            }
                        </div>
                        <div className="immployer__recommendations__immigration_options__timeline">
                            <div className="immployer__recommendations__immigration_options__timeline__item">
                                <div className="immployer__recommendations__immigration_options__timeline__item__circle_container">
                                    <SVGIcon src={Icons.CheckCircleFill} color="success" />
                                    <p>Request Made</p>
                                </div>
                                <div className="immployer__recommendations__immigration_options__timeline__item__line" />
                            </div>
                            <div className="immployer__recommendations__immigration_options__timeline__item">
                                <div className="immployer__recommendations__immigration_options__timeline__item__circle_container">
                                    {assessment?.questionnaire_status === 'finished'
                                        ? <SVGIcon src={Icons.CheckCircleFill} color="success" />
                                        : <div className="immployer__recommendations__immigration_options__timeline__item__circle" />
                                    }
                                    <p>{getForeignWorkerName()} to complete questionaire</p>
                                </div>
                                <div className="immployer__recommendations__immigration_options__timeline__item__line" />
                            </div>
                            <div className="immployer__recommendations__immigration_options__timeline__item">
                                <div className="immployer__recommendations__immigration_options__timeline__item__circle_container">
                                    {assessment?.questionnaire_status === 'finished'
                                        ? <SVGIcon src={Icons.CheckCircleFill} color="success" />
                                        : <div className="immployer__recommendations__immigration_options__timeline__item__circle" />
                                    }
                                    <p>Our team is preparing the report</p>
                                </div>
                                <div className="immployer__recommendations__immigration_options__timeline__item__line" />
                            </div>
                            <div className="immployer__recommendations__immigration_options__timeline__item">
                                <div className="immployer__recommendations__immigration_options__timeline__item__circle_container">
                                    {assessment?.report_url
                                        ? <SVGIcon src={Icons.CheckCircleFill} color="success" />
                                        : <div className="immployer__recommendations__immigration_options__timeline__item__circle" />
                                    }
                                    {!assessment?.report_url
                                        ? <p className="color_primary">Expected completion: {moment(assessment?.created_at).add(3, 'days').format('MMM DD, YYYY')}</p>
                                        : <p className="color_success">Report ready on {moment(assessment?.updated_at).format('MMM DD, YYYY')}</p>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="immployer__recommendations__legal__container">
                        <h4 className="pb_10 wt_500">Legal Team</h4>
                        <p>
                            <SVGIcon src={Icons.ClockCircle} color="primary" />
                            We’ll let you know in 24 hours what our legal team recommends
                        </p>
                        <div className="immployer__recommendations__legal__support_boxes">
                            <div className="immployer__recommendations__legal__support_box">
                                <div className="immployer__recommendations__legal__support_box__profile" />
                                <div className="immployer__recommendations__legal__support_box__info">
                                    <p className="color_dark"><strong>Blayne Kumar</strong></p>
                                    <p className='mice color_dark'>Immigration Consultant</p>
                                    <p className='mice color_grey'>12 years of experience</p>
                                    <p className='mice color_grey'>Country Expertise: Canada</p>
                                    <p className='mice color_grey'>Expertise: Study permits</p>
                                </div>
                            </div>
                            <div className="immployer__recommendations__legal__support_box">
                                <div className="immployer__recommendations__legal__support_box__profile" />
                                <div className="immployer__recommendations__legal__support_box__info">
                                    <p className="color_dark"><strong>Richard Coper</strong></p>
                                    <p className='mice color_dark'>Immigration Consultant</p>
                                    <p className='mice color_grey'>8 years of experience</p>
                                    <p className='mice color_grey'>Country Expertise: Canada</p>
                                    <p className='mice color_grey'>Expertise: Study permits</p>
                                </div>
                            </div>
                            <div className="immployer__recommendations__legal__support_box immployer__recommendations__legal__support_box--chat_info">
                                <SVGIcon src={Icons.Chat} color="primary" />
                                <div className="immployer__recommendations__legal__support_box__chat_info">
                                    <h4>Need help?</h4>
                                    <p className='mice color_dark'>Chat with us</p>
                                </div>
                                <button className="immployer__btn immployer__btn__primary immployer__btn__primary__disabled">
                                    Chat now
                                </button>
                            </div>
                        </div>
                    </div>
                    {!!isLoading &&
                        <div className="display_flex flex_dir_column display_flex_center width_full">
                            <span className="wt_600 pb_10">Loading Recommendations</span>
                            <i className='immployer__list_loader' />
                        </div>
                    }
                    {!isLoading && !assessmentPrograms?.length &&
                        <div className="display_flex flex_dir_column display_flex_center width_full">
                            <br />
                            <h4 className="color_dark text_center">No Recommendations yet.</h4>
                        </div>
                    }
                    {!isLoading && !!assessmentPrograms?.length &&
                        <Fragment>
                            <h4 className="pb_10 wt_500">Instant Recommendations</h4>
                            <div className="immployer__assessment_recommendations__list">
                                {
                                    assessmentPrograms.map(ap => {
                                        const cardInfo = getCardInfo(ap);

                                        return (
                                            <div key={ap.id} className="immployer__assessment_recommendation__card">
                                                <div className="immployer__arc__top_data">
                                                    <h3 className="immployer__arc__title">{ap.program.name}</h3>
                                                    {!!ap.program.subtitle && <p className="immployer__arc__subtitle">{ap.program.subtitle}</p>}
                                                    <p className="immployer__arc__descr">{getElipsisString(ap.program.description, 150)}</p>
                                                    {!!ap.program.gov_url && <Link className="immployer__arc__link" to={ap.program.gov_url} target="__blank">Learn more</Link>}
                                                </div>
                                                <div className="immployer__arc__bottom_data">
                                                    <div className="immployer__arc__bottom_left">
                                                        <span className={`immployer__arcbd__pill arcbd__pill__${ap.program.difficulty}`}>
                                                            {cardInfo.programDifficultyText}
                                                        </span>
                                                        <span className={`immployer__arcbd__pill arcbd__pill__${cardInfo.costDifficulty}`}>
                                                            {cardInfo.costDifficultyText}
                                                        </span>
                                                    </div>
                                                    <div className="immployer__arc__bottom_right">
                                                        <div className={`immployer__arcbd__timeframe arcbd__timeframe__${cardInfo.timeframeDifficulty}`}>
                                                            <div className="arcbd__timeframe__pie" />
                                                            <div className="arcbd__timeframe__title">{cardInfo.timeframe}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                            <br />
                            <BoxNote
                                code='primary'
                                content={(
                                    <p className="text_italic mice">
                                        Recommendations, estimated duration, cost, and difficulty may vary for each case.
                                    </p>
                                )}
                                maxWidth="500px"
                            />
                        </Fragment>
                    }
                </div>
            }
            {mainView === 'invite' &&
                <AssessmentInviteWorker
                    assessmentData={assessment ?? null}
                    resetMainView={() => setMainView('recommendations')}
                />
            }
        </AssessmentDetailsLayout>
    );
};

export default ImmigrationRecommendationsPage;